// What removing layers, a scene or declarations does to the declared game
// (7.6F): the explicit resolution the user is told about — never a silent
// repair (the Creative Engine still validates whatever is left). Kept apart
// from game.ts so element/scene deletion (commands.ts, scenes.ts) can use it.

import type { CreativeDocument, CreativeOperation, GameDecl } from "@interactads/creative-model";
import { omit } from "./util";

type Ops = CreativeOperation[];
const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

/** Game declaration without any role of `id` (a removed card takes its pair; empty blocks go). */
export function withoutRole(g: GameDecl, id: string): Record<string, unknown> {
  const next: Record<string, unknown> = { ...clone(g) };
  const targets = (g.targets ?? [])
    .filter((t) => t.element !== id)
    .map((t) => {
      return t.relocate === id ? omit(t, "relocate") : t;
    });
  if (targets.length > 0) next.targets = targets;
  else delete next.targets;
  if (g.pairs) {
    const cards = g.pairs.cards.filter((p) => !p.includes(id));
    if (cards.length > 0) next.pairs = { ...clone(g.pairs), cards };
    else delete next.pairs;
  }
  if (g.drops) {
    const items = g.drops.items.filter((x) => x !== id);
    const zones = g.drops.zones.filter((x) => x !== id);
    if (items.length > 0 && zones.length > 0) next.drops = { items, zones };
    else delete next.drops;
  }
  const r = g.runner;
  if (r) {
    // Without its player there is no runner (a finish goal goes with it); every other piece just leaves.
    if (r.player.element === id) {
      delete next.runner;
      if (g.win.when === "finish") next.win = { when: "manual" };
      return next;
    }
    const runner: Record<string, unknown> = { ...clone(r) };
    const layers = (r.layers ?? []).filter((l) => l.element !== id);
    if (layers.length > 0) runner.layers = layers;
    else delete runner.layers;
    runner.segments = r.segments.map((sg) => ({ ...sg, show: sg.show.filter((x) => x !== id) }));
    const items = (r.items ?? []).filter((i) => i.element !== id);
    if (items.length > 0) runner.items = items;
    else delete runner.items;
    const spawns = (r.spawns ?? [])
      .map((sp) => ({ ...sp, templates: sp.templates.filter((t) => t !== id) }))
      .filter((sp) => sp.templates.length > 0);
    if (spawns.length > 0) runner.spawns = spawns;
    else delete runner.spawns;
    const controls = Object.fromEntries(Object.entries(r.controls ?? {}).filter(([, v]) => v !== id));
    if (Object.keys(controls).length > 0) runner.controls = controls;
    else delete runner.controls;
    next.runner = runner;
  }
  return next;
}

/** Runner effects without a removed variable's key (energy / score / lives). */
function withoutEffect(runner: Record<string, unknown>, key: "energy" | "score" | "lives"): Record<string, unknown> {
  const strip = (e: Record<string, unknown>) => omit(e, key);
  const out = { ...runner };
  if (Array.isArray(out.items))
    out.items = (out.items as Array<Record<string, unknown>>).map((i) => ({
      ...i,
      effect: strip(i.effect as Record<string, unknown>),
    }));
  if (Array.isArray(out.spawns))
    out.spawns = (out.spawns as Array<Record<string, unknown>>).map((sp) => ({
      ...sp,
      effect: strip(sp.effect as Record<string, unknown>),
    }));
  return out;
}

// ─── Removal (explicit resolution, announced by the caller) ─────────────────

export interface GameRemoved {
  readonly elements?: readonly string[];
  readonly scene?: string;
  readonly variables?: readonly string[];
  readonly timers?: readonly string[];
}

/**
 * What removing elements / a scene / declarations does to the game: roles of
 * removed layers go (a card takes its pair), removed scenes leave the game's
 * scene list and results, removed score/lives/timer stop being used. Returns
 * no operation when the game is unaffected.
 */
export function pruneGame(doc: CreativeDocument, removed: GameRemoved): { ops: Ops; affected: boolean } {
  const g = doc.game;
  if (!g) return { ops: [], affected: false };
  let next = clone(g) as unknown as Record<string, unknown>;
  for (const id of removed.elements ?? []) next = withoutRole(next as unknown as GameDecl, id);
  const sc = removed.scene;
  if (sc !== undefined) {
    const scenes = (next.scenes as string[]).filter((x) => x !== sc);
    next.scenes = scenes;
    next.results = Object.fromEntries(
      Object.entries(next.results as Record<string, unknown>).filter(([, v]) => v !== sc)
    );
  }
  for (const v of removed.variables ?? []) {
    let runner = next.runner as Record<string, unknown> | undefined;
    if (next.score === v) {
      delete next.score;
      if (runner) runner = withoutEffect(runner, "score");
    }
    if (next.lives === v) {
      delete next.lives;
      next.scoring = { ...(next.scoring as Record<string, unknown>), missCostsLife: false };
      if (runner) runner = withoutEffect(runner, "lives");
    }
    if (runner && (runner.energy as { var?: string } | undefined)?.var === v) {
      delete runner.energy;
      runner = withoutEffect(runner, "energy");
    }
    if (runner) next.runner = runner;
  }
  for (const t of removed.timers ?? []) if (next.timer === t) delete next.timer;
  if (JSON.stringify(next) === JSON.stringify(g)) return { ops: [], affected: false };
  // A game left without any scene is removed (its layers and rules stay).
  if ((next.scenes as string[]).length === 0) return { ops: [{ op: "setGame" }], affected: true };
  return { ops: [{ op: "setGame", game: next as unknown as GameDecl }], affected: true };
}
